<script setup>
import { convertToInr } from "@/services/common-services";
import { barChartOptions, chartColors } from "@/services/constant-services";
import { useUserStore } from "@/store/user-store";
import { Knob } from "primevue";
import Chart from "primevue/chart";
import { computed } from "vue";

const store = useUserStore();

const data = computed(() => store.financialStatement || null).value;
const cibilScore = data.cibilScore;
const health = data.financialHealth;
const expenseData = data?.customerExpenses;
const incomeData = data?.customerIncome;
const fundstatus = data?.emergencyFundStatus;
const totalExpense = expenseData?.reduce(
  (total, item) => total + item.expAmt,
  0
);
const totalIncome = incomeData?.reduce(
  (total, item) => total + item.incomeAmt,
  0
);
const barData = {
  labels: ["Income", "Expense"],
  datasets: [
    {
      data: [totalIncome, totalExpense],
      label: ["Income vs Expense"],
      backgroundColor: chartColors.slice(0, 2),
    },
  ],
};
const bannerData = [
  {
    id: "netWorth",
    label: "Net Worth",
    value: convertToInr(health?.netWorth),
    tooltip: "Total Assets - Total Liabilitites",
  },
  {
    id: "netIncome",
    label: "Net Income",
    value: convertToInr(health?.netIncome),
    tooltip: "Total Income - Total Expenses",
  },
  {
    id: "savingsRatio",
    label: "Savings Ratio",
    value: health?.savingsRatio,
    tooltip: "(Net Income + Savings) / Total Income",
  },
  {
    id: "debtRatio",
    label: "Debt to Income Ratio",
    value: health?.debtToIncomeRatio,
    tooltip: "Total Liabilities / Total Income",
  },
];

const cibilColor = (score) =>
  score >= 700
    ? "var(--an-success)"
    : score >= 650
    ? "var(--an-tertiary)"
    : score >= 600
    ? "var(--an-average)"
    : "var(--an-error)";

const fundProgressColor = (value) =>
  value >= 75
    ? "var(--an-success)"
    : value >= 50
    ? "var(--an-tertiary)"
    : value >= 25
    ? "var(--an-average)"
    : "var(--an-error)";
</script>

<template>
  <div class="mt-30">
    <span class="font-20 font-weight-700">Personal Finance Snapshot</span>
    <section
      class="w-100 mt-20 d-flex flex-direction-column input-shadow br-10 pv-15"
    >
      <div class="flex-1 d-flex">
        <div
          class="d-flex flex-direction-column align-items-center justify-center p-5 flex-1"
        >
          <span class="font-20 text-center font-weight-600">Cibil Score</span>
          <div class="position-relative">
            <Knob
              v-model="cibilScore"
              value-template=""
              readonly
              :value-color="cibilColor(cibilScore)"
              range-color="var(--an-background-light)"
              :stroke-width="5"
              :min="0"
              :size="80"
              :max="900"
              class="d-flex align-items-center"
            />
            <span
              class="position-absolute dead-center font-weight-500 text-center d-flex align-items-center justify-center"
              >{{ cibilScore }}
            </span>
          </div>
        </div>

        <span class="v-divider mv-10" />
        <template v-for="(item, index) in bannerData">
          <div
            class="d-flex flex-direction-column justify-center p-5 gap-5 flex-1"
          >
            <span
              class="text-center font-weight-600 d-flex align-items-center justify-center gap-5"
              >{{ item.label }}
              <i
                class="pi pi-info-circle font-15"
                v-if="item.tooltip && index !== bannerData.length - 1"
                v-tooltip.top="{
                  value: item.tooltip,
                  class: 'bottom-arrow',
                }" />
              <i
                class="pi pi-info-circle font-15"
                v-else
                v-tooltip.left="{
                  value: item.tooltip,
                  class: 'right-arrow',
                }"
            /></span>
            <span class="font-25 font-weight-500 text-center"
              >{{ item.value }}
            </span>
          </div>
          <span class="v-divider mv-10" v-if="index < bannerData.length - 1" />
        </template>
      </div>
    </section>

    <div class="d-flex mt-20 gap-20">
      <div
        class="d-flex flex-direction-column flex-1 input-shadow br-10 ph-20 pt-20 pb-10"
      >
        <span class="font-20 font-weight-700">Income vs Expense</span>
        <Chart
          type="bar"
          :data="barData"
          :options="barChartOptions"
          class="w-100 mt-20 flex-1"
        />
      </div>
      <div
        class="flex-1 input-shadow br-10 p-20 d-flex flex-direction-column gap-15"
      >
        <span class="font-20 font-weight-700">Emergency Fund Status</span>
        <div v-if="fundstatus" class="d-flex felx-direction-column align-items-center justify-center">
          <div class="barOverflow position-relative">
            <div
              class="bar"
              :style="{
                transform: `rotate(${
                  45 + fundstatus.percentageAchieved.split(' ')[0] * 1.8
                }deg)`,
                borderBottomColor: fundProgressColor(
                  fundstatus.percentageAchieved.split(' ')[0]
                ),
                borderRightColor: fundProgressColor(
                  fundstatus.percentageAchieved.split(' ')[0]
                ),
              }"
            ></div>
            <div
              class="position-absolute l-0 r-0 b-0 m-auto w-max h-max d-flex flex-direction-column align-items-center"
            >
              <span class="font-20 font-weight-600">{{
                fundstatus.percentageAchieved
              }}</span>
              <span class="font-15 font-weight-500">Percentage Acheived</span>
            </div>
          </div>
        </div>
        <div v-else class="d-flex flex-direction-column align-items-center justify-center p-20">
          <i class="pi pi-info-circle font-30 mb-10" style="color: var(--an-text-secondary);"></i>
          <span class="font-16 font-weight-500 text-center" style="color: var(--an-text-secondary);">
            Emergency fund information not available
          </span>
          <span class="font-14 text-center mt-5" style="color: var(--an-text-muted);">
            Please update your financial information to see your emergency fund status
          </span>
        </div>
        <span class="h-divider" v-if="fundstatus" />
        <div class="d-flex" v-if="fundstatus">
          <div class="d-flex flex-direction-column flex-1 align-items-center">
            <span class="font-weight-500">Recommended Balance</span>
            <span class="font-20 font-weight-600 text-success">{{
              convertToInr(fundstatus.recommendedBalance)
            }}</span>
          </div>
          <span class="v-divider" />
          <div class="d-flex flex-direction-column flex-1 align-items-center">
            <span class="font-weight-500">Current Balance</span>
            <span
              class="font-20 font-weight-600 text-primary"
              :style="{
                color:
                  fundstatus.currentBalance < fundstatus.recommendedBalance
                    ? 'var(--an-error)'
                    : 'var(--an-success)',
              }"
              >{{ convertToInr(fundstatus.currentBalance) }}</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.snapshot-grid {
  background-color: var(--an-gray1);
  gap: 3px;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: repeat(2, 1fr);
}

.barOverflow {
  overflow: hidden;
  width: 200px;
  height: 100px;
}
.bar {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 15px solid var(--an-background-light);
}
</style>
